import { t } from '@lingui/macro';
import {
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { FormResponses_content$data } from './__generated__/FormResponses_content.graphql';
import { FormQuestionType } from 'components/icons/QuestionIcon';
import { useI18n } from 'utils/i18n';

interface useFormResponsesTableProps {
  questions: FormResponses_content$data['questions'];
  responses: FormResponses_content$data['responses'];
}

interface Response {
  member: string;
  respondedAt: string;

  [key: string]: string;
}

export const useFormResponsesTable = ({
  questions,
  responses,
}: useFormResponsesTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const { locale, format } = useI18n();

  const columns = useMemo(() => {
    const questionsHeaders =
      questions
        ?.reduce((acc, question) => {
          if (
            question.definition?.__typename === FormQuestionType.CHECKBOXES ||
            question.definition?.__typename === FormQuestionType.MULTIPLECHOICE
          ) {
            return [
              ...acc,
              ...(question?.definition?.options?.map((option) => ({
                id: `${question.id}_${option.value}`,
                label: `${question.text} - ${option.text}`,
              })) || []),
            ];
          }

          return [...acc, { id: question.id, label: question.text }];
        }, [] as Array<{ id: string; label: string }>)
        .map((question) => ({
          accessorKey: question.id,
          header: question.label,
        })) || [];

    return [
      {
        header: t`Member`,
        accessorKey: 'member',
      },
      {
        header: t`Responded At`,
        accessorKey: 'respondedAt',
        cell: ({ getValue }) => format(getValue(), 'PPPp'),
      },
      ...questionsHeaders,
    ] as ColumnDef<Response>[];
  }, [questions, locale]);

  const data: Response[] = useMemo(
    () =>
      responses?.edges.map((response) => {
        const questionResponses = response.node?.answers.reduce(
          (acc, answer) => {
            const question = questions?.find(
              (question) => question.id === answer.questionId,
            );

            switch (question?.definition?.__typename) {
              case 'CustomField__Checkboxes':
                return {
                  ...acc,
                  ...question?.definition?.options?.reduce(
                    (acc, option) => ({
                      ...acc,
                      [`${question.id}_${option.value}`]:
                        answer.value?.__typename ===
                          'CustomValue__Checkboxes' &&
                        answer.value?.checkboxValues?.includes(option.value)
                          ? '✓'
                          : '-',
                    }),
                    {},
                  ),
                };

              case FormQuestionType.MULTIPLECHOICE:
                return {
                  ...acc,
                  ...question?.definition?.options?.reduce(
                    (acc, option) => ({
                      ...acc,
                      [`${question.id}_${option.value}`]:
                        answer.value?.__typename ===
                          'CustomValue__MultipleChoice' &&
                        answer.value?.multipleChoiceValue === option.value
                          ? '✓'
                          : '-',
                    }),
                    {},
                  ),
                };

              case 'CustomField__String':
                return {
                  ...acc,
                  [question.id]:
                    answer.value?.__typename === 'CustomValue__String' &&
                    answer.value?.stringValue,
                };

              case 'CustomField__Numeric':
                return {
                  ...acc,
                  [question.id]:
                    answer.value?.__typename === 'CustomValue__Numeric' &&
                    answer.value?.numericValue,
                };

              case 'CustomField__Date': {
                const dateSuffix = 'T00:00:00'; // Fixes the issue of dates with one-day off in different timezones
                return {
                  ...acc,
                  [question.id]:
                    answer.value?.__typename === 'CustomValue__Date' &&
                    answer.value?.dateValue &&
                    format(
                      new Date(answer.value.dateValue + dateSuffix),
                      'PPP',
                    ),
                };
              }
              case 'CustomField__Time':
                return {
                  ...acc,
                  [question.id]:
                    answer.value?.__typename === 'CustomValue__Time' &&
                    answer.value?.timeValue &&
                    format(
                      new Date('1970-01-01T' + answer.value.timeValue),
                      'p',
                    ),
                };

              case 'CustomField__DateTime':
                return {
                  ...acc,
                  [question.id]:
                    answer.value?.__typename === 'CustomValue__DateTime' &&
                    answer.value?.dateTimeValue &&
                    format(new Date(answer.value.dateTimeValue), 'PPPp'),
                };

              case 'CustomField__Email':
                return {
                  ...acc,
                  [question.id]:
                    answer.value?.__typename === 'CustomValue__Email' &&
                    answer.value?.emailValue,
                };

              case 'CustomField__PhoneNumber':
                return {
                  ...acc,
                  [question.id]:
                    answer.value?.__typename === 'CustomValue__PhoneNumber' &&
                    answer.value?.phoneNumberValue,
                };

              case 'CustomField__Link':
                return {
                  ...acc,
                  [question.id]:
                    answer.value?.__typename === 'CustomValue__Link' &&
                    answer.value?.linkValue,
                };

              default:
                return acc;
            }
          },
          {},
        );

        return {
          member: response.node?.responder?.name || '',
          respondedAt: response.node?.respondedAt || '',
          ...questionResponses,
        };
      }) || [],
    [questions],
  );

  const { getHeaderGroups, getRowModel } = useReactTable<Response>({
    columns,
    data,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return {
    getHeaderGroups,
    getRowModel,
    columns,
    data,
  };
};
